import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Avatar, Box } from '@mui/material';
import { listAccessibleGroupsAPI } from '../../../store/features/listGroup';
import SearchInput from '../../common/SearchInputField';
import useClickOutside from '../../../hooks/useClickOutside';
import { colorCodesForAvatar } from '../../../constants/index';
import {
  getCachedAccessibleGroups,
  setCachedAccessibleGroups,
} from '../../../utils/accessibleGroupsCache';
import { ReactComponent as Dropdown } from "../../../assets/images/dropdown-arrow.svg";
import './style.css'
import NoSearch from '../../common/NoSearch';

const DEFAULT_WIDTH = 296;

const triggerStyle = {
  color: '#405261',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  borderRadius: '8px',
  border: '1px solid #D5DCE1',
  background: '#FFF',
  boxShadow: '0 1px 2px 0 rgba(6, 21, 34, 0.05)',
  height: '40px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  padding: '0 12px',
  cursor: 'pointer',
  boxSizing: 'border-box',
};

const menuStyle = {
  flexShrink: 0,
  maxHeight: '224px',
  overflowY: 'auto',
  borderRadius: '8px',
  border: '1px solid #D5DCE1',
  background: '#FFF',
  boxShadow: '0 15px 20px 0 rgba(6, 21, 34, 0.05)',
  position: 'absolute',
  top: '48px',
  right: 0,
  zIndex: 1400,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
};

const listStyle = {
  overflowY: 'auto',
  padding: '0px 8px 4px 8px',
  flex: 1,
};

const getAvatarColor = (name) => {
  if (!name) return colorCodesForAvatar[0];
  const first = name[0].toUpperCase();
  const idx = first.charCodeAt(0) - 65;
  return colorCodesForAvatar[(idx >= 0 && idx < colorCodesForAvatar.length) ? idx : 0];
};

function GroupSelectDropdown({ onSelect, value, width = DEFAULT_WIDTH }) {
  const dispatch = useDispatch();
  const cachedGroups = getCachedAccessibleGroups();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState(() =>
    Array.isArray(cachedGroups) ? cachedGroups : []
  );
  // Soft refresh when cache exists — do not flip UI visibility on refetch.
  const [loading, setLoading] = useState(() => !Array.isArray(cachedGroups));
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const wrapperRef = useRef(null);
  const resolvedWidth = width === '100%' ? '100%' : `${Number(width) || DEFAULT_WIDTH}px`;

  useClickOutside(wrapperRef, () => {
    setOpen(false)
    setSearch('')
  });

  useEffect(() => {
    const hasCache = Array.isArray(getCachedAccessibleGroups());
    if (!hasCache) setLoading(true);

    dispatch(listAccessibleGroupsAPI()).then((res) => {
      if (res?.payload?.status === 200 && Array.isArray(res?.payload?.data)) {
        const next = res.payload.data;
        setGroups(next);
        setCachedAccessibleGroups(next);
      }
    }).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (!groups.length) return;

    if (value) {
      const found = groups.find((g) => (g.id || g._id) === value);
      if (found) {
        setSelected(found);
      } else {
        // Stale id from a previous business/session — allow defaulting again.
        setSelected(null);
        setInitialized(false);
      }
    }
  }, [value, groups]);

  // Prefer controlled value; only default to "general" when parent has no selection.
  useEffect(() => {
    if (initialized || groups.length === 0) return;

    if (value) {
      const found = groups.find((g) => (g.id || g._id) === value);
      if (found) {
        setSelected(found);
        setInitialized(true);
        return;
      }
    }

    const general = groups.find((g) => (g.title || '').toLowerCase() === 'general');
    const fallback = general || groups[0];
    if (fallback) {
      setSelected(fallback);
      onSelect && onSelect(fallback.id || fallback._id);
      setInitialized(true);
    }
  }, [groups, initialized, onSelect, value]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter((g) => g.title?.toLowerCase().includes(term));
  }, [groups, search]);

  const triggerLabel = useMemo(() => {
    if (selected?.title) return selected.title;
    if (value && groups.length > 0) {
      const found = groups.find((g) => (g.id || g._id) === value);
      if (found) return found.title;
    }
    if (loading && groups.length === 0) return 'Loading groups...';
    if (!groups.length) return 'No groups available';
    return 'Select Group';
  }, [loading, selected, value, groups]);

  const handleSelect = (g) => {
    setSelected(g);
    onSelect && onSelect(g.id || g._id);
    setOpen(false);
  };

  // 0–1 groups: never show. 2+: show immediately from cache (no hide→appear).
  // First visit with no cache still waits for fetch once, then caches for next mounts.
  if (groups.length <= 1) {
    return null;
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: resolvedWidth, zIndex: open ? 1400 : 'auto' }}>
      <div
        style={{
          ...triggerStyle,
          width: resolvedWidth,
          cursor: loading ? 'default' : 'pointer',
          color: loading ? '#93A1B0' : triggerStyle.color,
        }}
        onClick={() => {
          if (!loading) setOpen((v) => !v);
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {triggerLabel}
        </span>
        <Dropdown style={{ marginLeft: 'auto', width: '16px', height: '16px' }} />
      </div>

      {open && (
        <div style={{ ...menuStyle, width: resolvedWidth }}>
          <div style={{ padding: '8px' }}>
            <SearchInput
              title=""
              placeholder="Search group"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              noTitle
              style={{ width: '100%', height: '40px' }}
              inputStyle={{
                width: '100%',
                height: '40px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {filtered.length === 0 && search && (
            <NoSearch
              title="No groups found"
              description="Clear your search and try again."
              titleFontSize={14}
              descriptionFontSize={12}
            />
          )}
          <div style={listStyle}>
            {filtered.map((g) => {
              const selectedValue = selected ? (selected.id || selected._id) : null;
              const gValue = g.id || g._id;
              const isSelected = selectedValue === gValue;

              return (
                <Box
                  key={g.id || g._id}
                  onClick={() => handleSelect(g)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: isSelected ? "#061522" : "#556575",
                    backgroundColor: isSelected ? "#F9FAFA" : "transparent",
                    transition: "background-color 0.2s ease, color 0.2s ease",

                    "&:hover": {
                      backgroundColor: "#F9FAFA",
                      color: "#061522",
                    },
                  }}
                >
                  {g.group_image ? (
                    <Avatar
                      src={g.group_image}
                      alt={g.title}
                      sx={{ width: 24, height: 24, fontSize: "12px" }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: getAvatarColor(g.title),
                        color: "#fff",
                        fontSize: "12px",
                      }}
                    >
                      {g.title?.[0]?.toUpperCase()}
                    </Avatar>
                  )}

                  <Box
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Inter",
                      fontWeight: 400,
                      lineHeight: "normal",
                      color: "inherit",
                    }}
                  >
                    {g.title}
                  </Box>
                </Box>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupSelectDropdown;
